import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import LeaveApprovalDetailMobile from "./LeaveApprovalDetailMobile";
import "./LeaveApproval.scss";

interface LeaveApprovalDetailPageProps {}

const LeaveApprovalDetailPage: React.FC<LeaveApprovalDetailPageProps> = () => {
  const { id } = useParams();
  const routes = all_routes;
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // If mobile, render mobile component
  if (isMobile) {
    return <LeaveApprovalDetailMobile />;
  }

  // Sample leave data - in real app this would come from API based on ID
  const leaveData = {
    id: parseInt(id || '15136'),
    title: "Approve Special CL Sterilization, RAGHUVEER PRASAD",
    dateRange: "24.07.2025 - 24.07.2025",
    status: "New",
    employee: {
      name: "RAGHUVEER PRASAD",
      designation: "Sys.Analyst",
      id: "15136",
      department: "IT Department",
      reportingManager: "MANAGER A",
      contactNumber: "+91-9876543210",
      email: "raghuveer.prasad@dmrc.gov.in",
      joiningDate: "15-Jan-2020",
      timeStamp: "09.06.2025 16:17:33"
    },
    approver: {
      name: "OSHO ADITYA SAXENA",
      designation: "MANAGER/IT",
      id: "20990",
      timeStamp: "13.06.2025 09:58:12"
    },
    leaveDetails: {
      type: "Special CL Sterilization",
      subType: "Medical Procedure",
      startDate: "24-07-2025",
      endDate: "24-07-2025",
      totalDays: 1,
      totalWorkingDays: 1,
      reason: "Special CL for sterilization procedure as per medical recommendation.",
      addressDuringLeave: "Home Address, Noida",
      cityDistrict: "Noida, Uttar Pradesh",
      contactDuringLeave: "+91-9876543210",
      appliedDate: "23-07-2025",
      sterilizationOfSelf: true,
      emergencyContact: "Family Member - +91-9876543211"
    },
    leaveBalance: {
      totalMedical: 12,
      usedMedical: 3,
      remainingMedical: 9,
      totalCasual: 8,
      usedCasual: 2,
      remainingCasual: 6,
      totalAnnual: 21,
      usedAnnual: 5,
      remainingAnnual: 16
    },
    attachments: [
      {
        name: "medical_certificate.pdf",
        size: "1.2 MB",
        uploadDate: "23-07-2025",
        type: "Medical Certificate"
      }
    ],
    leaveHistory: [
      {
        date: "15-06-2025",
        type: "Casual Leave",
        days: 1,
        status: "Approved",
        reason: "Personal work"
      },
      {
        date: "10-05-2025", 
        type: "Medical Leave",
        days: 2,
        status: "Approved",
        reason: "Medical checkup"
      }
    ]
  };

  const getLeaveTypeBadgeClass = (leaveType: string) => {
    switch (leaveType) {
      case 'Medical Leave':
        return 'badge-soft-danger';
      case 'Casual Leave':
        return 'badge-soft-warning';
      case 'Annual Leave':
        return 'badge-soft-success';
      case 'Sick Leave':
        return 'badge-soft-info';
      case 'Special CL Sterilization':
        return 'badge-soft-purple';
      default:
        return 'badge-soft-secondary';
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb Header - Consistent with portal */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Leave Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.todoWorklist.tasks}>To Do Work List</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Leave Details
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="me-2 mb-2">
              <Link 
                to={routes.todoWorklist.tasks}
                className="btn btn-outline-light d-flex align-items-center"
              >
                <i className="ti ti-arrow-left me-2"></i>
                Back to List
              </Link>
            </div>
          </div>
        </div>

        {/* Title Card - Consistent with portal card structure */}
        <div className="card mb-4">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3 mt-2">{leaveData.title}</h4>
            <div className="d-flex align-items-center mb-3">
              <span className="badge badge-soft-primary me-2">ID: {leaveData.id}</span>
              <span className="badge badge-soft-warning">{leaveData.status}</span>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Main Content */}
          <div className="col-lg-8">
            {/* Employee Information */}
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3 mt-2">
                  <i className="ti ti-user me-2"></i>
                  Employee Information
                </h4>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Employee Name</label>
                      <div className="fw-medium text-dark">{leaveData.employee.name}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Employee</label>
                      <div className="fw-medium text-dark">{
                        leaveData.employee.id 
                          ? `${parseInt(leaveData.employee.id, 10)} - ${leaveData.employee.name || 'Unknown'}`
                          : leaveData.employee.name || leaveData.employee.id
                      }</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Designation</label>
                      <div className="fw-medium text-dark">{leaveData.employee.designation}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Department</label>
                      <div className="fw-medium text-dark">{leaveData.employee.department}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Reporting Manager</label>
                      <div className="fw-medium text-dark">{leaveData.employee.reportingManager}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Contact Number</label>
                      <div className="fw-medium text-dark">{leaveData.employee.contactNumber}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Email</label>
                      <div className="fw-medium text-dark">{leaveData.employee.email}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Applied On</label>
                      <div className="fw-medium text-dark">{leaveData.employee.timeStamp}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Leave Details */}
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3 mt-2">
                  <i className="ti ti-calendar-event me-2"></i>
                  Leave Details
                </h4>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Leave Type</label>
                      <div>
                        <span className={`badge ${getLeaveTypeBadgeClass(leaveData.leaveDetails.type)} me-2`}>
                          {leaveData.leaveDetails.type}
                        </span>
                        {leaveData.leaveDetails.subType && (
                          <span className="badge badge-soft-secondary">{leaveData.leaveDetails.subType}</span>
                        )}
                      </div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Start Date</label>
                      <div className="fw-medium text-dark">{leaveData.leaveDetails.startDate}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">End Date</label>
                      <div className="fw-medium text-dark">{leaveData.leaveDetails.endDate}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Total Days</label>
                      <div>
                        <span className="badge badge-soft-primary">{leaveData.leaveDetails.totalDays} days</span>
                        <small className="text-muted ms-2">({leaveData.leaveDetails.totalWorkingDays} working days)</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Address During Leave</label>
                      <div className="fw-medium text-dark">{leaveData.leaveDetails.addressDuringLeave}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">City/District</label>
                      <div className="fw-medium text-dark">{leaveData.leaveDetails.cityDistrict}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Contact During Leave</label>
                      <div className="fw-medium text-dark">{leaveData.leaveDetails.contactDuringLeave}</div>
                    </div>
                    <div className="form-wrap mb-3">
                      <label className="col-form-label">Emergency Contact</label>
                      <div className="fw-medium text-dark">{leaveData.leaveDetails.emergencyContact}</div>
                    </div>
                  </div>
                </div>
                
                <div className="form-wrap">
                  <label className="col-form-label">Reason for Leave</label>
                  <div className="bg-light-gray p-3 rounded border">
                    <p className="mb-0 text-dark">{leaveData.leaveDetails.reason}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Leave Balance */}
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3 mt-2">
                  <i className="ti ti-calendar-stats me-2"></i>
                  Leave Balance
                </h4>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-4">
                    <div className="bg-light-gray p-3 rounded border">
                      <div className="text-danger mb-2">
                        <i className="ti ti-heart-plus" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="text-danger mb-2">Medical Leave</h5>
                      <p className="mb-1">
                        <span className="fw-bold h4">{leaveData.leaveBalance.remainingMedical}</span>
                        <span className="text-muted">/{leaveData.leaveBalance.totalMedical}</span>
                      </p>
                      <small className="text-muted">Available</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="bg-light-gray p-3 rounded border">
                      <div className="text-warning mb-2">
                        <i className="ti ti-calendar-time" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="text-warning mb-2">Casual Leave</h5>
                      <p className="mb-1">
                        <span className="fw-bold h4">{leaveData.leaveBalance.remainingCasual}</span>
                        <span className="text-muted">/{leaveData.leaveBalance.totalCasual}</span>
                      </p>
                      <small className="text-muted">Available</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="bg-light-gray p-3 rounded border">
                      <div className="text-success mb-2">
                        <i className="ti ti-calendar-check" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="text-success mb-2">Annual Leave</h5>
                      <p className="mb-1">
                        <span className="fw-bold h4">{leaveData.leaveBalance.remainingAnnual}</span>
                        <span className="text-muted">/{leaveData.leaveBalance.totalAnnual}</span>
                      </p>
                      <small className="text-muted">Available</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3 mt-2">
                  <i className="ti ti-paperclip me-2"></i>
                  Attachments ({leaveData.attachments.length})
                </h4>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Document Name</th>
                        <th>Type</th>
                        <th>Size</th>
                        <th>Upload Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveData.attachments.map((attachment, index) => (
                        <tr key={index}>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className="ti ti-file-text text-danger me-2 fs-4"></i>
                              <span className="fw-medium">{attachment.name}</span>
                            </div>
                          </td>
                          <td>{attachment.type}</td>
                          <td>{attachment.size}</td>
                          <td>{attachment.uploadDate}</td>
                          <td>
                            <Link to="#" className="btn btn-outline-primary btn-sm">
                              <i className="ti ti-download me-1"></i>
                              Download
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Recent Leave History */}
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3 mt-2">
                  <i className="ti ti-history me-2"></i>
                  Recent Leave History
                </h4>
              </div>
              <div className="card-body p-0 py-3">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Days</th>
                        <th>Status</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveData.leaveHistory.map((leave, index) => (
                        <tr key={index}>
                          <td>{leave.date}</td>
                          <td>
                            <span className={`badge ${getLeaveTypeBadgeClass(leave.type)}`}>
                              {leave.type}
                            </span>
                          </td>
                          <td>{leave.days}</td>
                          <td>
                            <span className="badge badge-soft-success">{leave.status}</span>
                          </td>
                          <td>{leave.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Consistent with portal sidebar styling */}
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3 mt-2">
                  <i className="ti ti-info-circle me-2"></i>
                  Quick Summary
                </h4>
              </div>
              <div className="card-body">
                <div className="border-bottom pb-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Employee:</span>
                    <span className="fw-medium">{leaveData.employee.name}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Leave Type:</span>
                    <span className="fw-medium">{leaveData.leaveDetails.type}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Duration:</span>
                    <span className="fw-medium">{leaveData.leaveDetails.totalDays} days</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Medical Balance:</span>
                    <span className="fw-medium text-success">{leaveData.leaveBalance.remainingMedical} available</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Status:</span>
                    <span className="badge badge-soft-warning">Pending Approval</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                  <Link 
                    to={routes.todoWorklist.tasks}
                    className="btn btn-primary"
                  >
                    <i className="ti ti-arrow-left me-2"></i>
                    Back to Worklist
                  </Link>
                  <div className="text-center mt-2">
                    <small className="text-muted">
                      Use approve/reject buttons in the worklist to take action
                    </small>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="border-top pt-3 mt-3">
                  <h6 className="fw-medium mb-3">Request Details</h6>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Request ID:</span>
                    <span className="fw-medium">{leaveData.id}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Applied Date:</span>
                    <span className="fw-medium">{leaveData.leaveDetails.appliedDate}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Working Days:</span>
                    <span className="fw-medium">{leaveData.leaveDetails.totalWorkingDays} days</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveApprovalDetailPage;
